import { observable, action, computed } from 'mobx';
import { inject, provideSingleton } from '../utils/decorators';
import * as ApiTypes from '../models/ApiTypes';
import * as ApiTypesSes from '../models/ApiTypesSes';
import { ToastStore } from './ToastStore';
import { IPublicAuftrag } from '../models/ApiTypesPublic';
import { IBpsReferenzkraftMesswerte, IBpsReferenzkraftMesswerteResponse, IBpsReferenzkraftBeurteilungRequest, IBpsReferenzkraftBeurteilungResponse } from '../models/ApiTypesBps';
import * as ApiTypesBps2021 from '../models/ApiTypesBps2021';
import * as ApiTypesBpsPlatte from '../models/ApiTypesBpsPlatte';
import * as ApiTypesAgtPartikelzaehler from '../models/ApiTypesAgtPartikelzaehler';

export const TOKEN_SESSION_KEY = 'token';

// tslint:disable-next-line:no-any
const createJsonBlob = (data: any) => {
  return new Blob([JSON.stringify(data)], {type : 'application/json'});
};

const apiRoutes = {
  login: '/api/login',
  logout: '/api/logout',
  arbeitsmittel: '/api/aw/arbeitsmittel',
  orte: '/api/aw/orte',
  profil: '/api/profile',
  freigaben: '/api/profile/freigaben',
  objekte: '/api/aw/objekte',
  befugnisse: '/api/profile/befugnisse',
  dienstleistungen: '/api/profile/dienstleistungen',
  personalliste: '/api/user/personalliste',
  personallisteKategorie: '/api/user/personalliste',
  qmdokumentation: '/api/user/qmdokumentation',
  auftraege: '/api/aw/auftraege',
  lizenzauftraege: '/api/aw/lizenz/auftraege',
  vorgaenge: '/api/aw/vorgaenge',
  beurteilungaufstellflaecheFahrzeug: '/api/labor/ses/beurteilungaufstellflaeche/fahrzeug',
  beurteilungaufstellflaecheSep: '/api/labor/ses/beurteilungaufstellflaeche/sep',
  beurteilungaufstellflaecheKalibrierschein: '/api/labor/ses/beurteilungaufstellflaeche/kalibrierschein',
  auswertungMesswerte: '/api/labor/ses/beurteilungaufstellflaeche/auswertung',
  messwerteSesVorhanden: '/api/labor/ses/messwerte',
  versionsinfo: '/api/labor/versionsinfo',
  rechnungspositionen: '/api/aw/rechnungen/rechnungspositionen',
  rechnungsuebersicht: '/api/rechnungen/uebersicht',
  rechnungsdetail: '/api/rechnungen/rechnung',
  rechnungenauftrag: '/api/rechnungen/auftrag',
  rueckmeldung: '/api/rueckmeldung'
};

/**
 * Kapselklasse für Api/Backend Aufrufe
 */
@provideSingleton(ApiStore)
export class ApiStore {

  @observable token: string = '';
  @observable requestCount = 0;
  @observable sessionMessage: string = '';
  @inject(ToastStore) toastStore: ToastStore;

  constructor() {
    this.loadToken();
  }

  @action incrementRequestCount() {
    this.requestCount++;
  }

  @action decrementRequestCount() {
    if (this.requestCount <= 0) {
      this.requestCount = 0;
    } else {
      this.requestCount--;
    }
  }

  @computed get isLoading() {
    return this.requestCount > 0;
  }

  @action loadToken() {
    let storedToken = window.localStorage.getItem(TOKEN_SESSION_KEY);
    if (storedToken && storedToken.length > 0) {
      this.token = storedToken;
      this.sessionMessage = '';
    }
  }

  @action setToken(token: string) {
    if (token.length === 0) {
      throw new Error('Do not set empty tokens!');
    }
    window.localStorage.setItem(TOKEN_SESSION_KEY, token);
    this.token = token;
  }

  @action deleteToken() {
    window.localStorage.removeItem(TOKEN_SESSION_KEY);
    this.token = '';
    this.sessionMessage = '';
  }

  @action setSessionMessage(message: string) {
    this.sessionMessage = message;
  }

  private getDefaultRequestOptions(): RequestInit {
    return (this.token && this.token.length > 0) ? {
      headers: {
        'Accept': 'application/json',
        'Authorization': 'Bearer ' + this.token
      }
    } : {};
  }

  private getRouteErrorLabel(route: string) {

    if (route.startsWith(apiRoutes.auftraege)) {
      return 'beim Laden des Auftrags';
    }

    switch (route) {
      case apiRoutes.login:
        return 'beim Login';
      case apiRoutes.logout:
        return 'beim Logout';
      case apiRoutes.arbeitsmittel:
        return 'beim Laden der Arbeitsmittel';
      case apiRoutes.profil:
        return 'beim Laden des Benutzerprofils';
      case apiRoutes.befugnisse:
        return 'beim Laden der Befugnisse';
      case apiRoutes.dienstleistungen:
        return 'beim Laden der Dienstleistungen';
      case apiRoutes.dienstleistungen:
        return 'beim Laden der Aufträge';
      case apiRoutes.beurteilungaufstellflaecheFahrzeug:
      case apiRoutes.beurteilungaufstellflaecheSep:
        return 'beim Validieren der Messwerte';
      case apiRoutes.auswertungMesswerte:
        return 'beim Erzeugen der Auswertung';
      case apiRoutes.freigaben:
        return 'beim Laden der Freigaben';
      case apiRoutes.personalliste:
        return 'beim Laden der Personalliste';
      case apiRoutes.qmdokumentation:
        return 'beim Laden der QM-Dokumentation';
      default:
        return route;
    }
  }

  private async request<T>(route: string, requestOptions: RequestInit): Promise<ApiTypes.IResponse<T>> {
    let response;
    try {
      this.incrementRequestCount();
      response = await window.fetch(
        route,
        Object.assign({}, this.getDefaultRequestOptions(), requestOptions)
      );
    } catch (e) {
      this.decrementRequestCount();
      const message = `Es ist ein Fehler ${this.getRouteErrorLabel(route)} aufgetreten.`;
      this.toastStore.show({
        kind: 'error',
        message: message,
        details: (e as Error).message
      });
      return {
        ok: false,
        error: message
      };
    }

    // Server Session abgelaufen => lokales token löschen
    if ((response.status === 401 || response.status === 400) && route !== apiRoutes.login) {
      this.deleteToken();
    }

    this.decrementRequestCount();

    let responseJson;
    try {
      responseJson = await response.json();
    } catch (e) {
      const message = `Es ist ein Fehler ${this.getRouteErrorLabel(route)} aufgetreten.`;
      this.toastStore.show({
        kind: 'error',
        message: message,
        details: (e as Error).message
      });
      return {
        ok: false,
        error: message
      };
    }

    if ('error' in responseJson) {
      // Bestimmte Antworten, z.B. Sitzungsbehandlung, werden gesondert behandelt und sollen keine Standard-Fehlermeldung anzeigen
      let showToast: boolean = true;
      if (responseJson.error) {
        switch (responseJson.error) {
          case 'session_expired':
            this.setSessionMessage('Sitzung abgelaufen. Bitte melden Sie sich neu an.');
            showToast = false;
            break;
          case 'session_invalid':
            this.setSessionMessage('Sitzung ungültig. Bitte melden Sie sich neu an.');
            showToast = false;
            break;
          default:
            this.setSessionMessage('');
        }
      }
      if (showToast) {
        this.toastStore.show({
          kind: 'error',
          message: responseJson.error,
          details: JSON.stringify(responseJson)
        });
      }
      return {
        ok: false,
        error: responseJson.error
      };
    } else if (response.status !== 200) {
      const message = `Es ist ein Fehler ${this.getRouteErrorLabel(route)} aufgetreten.`;
      this.toastStore.show({
        kind: 'error',
        message: message,
        details: JSON.stringify(responseJson)
      });
      return {
        ok: false,
        error: message
      };
    } else {
      return {
        ok: true,
        json: responseJson
      };
    }
  }

  async login(username: string, password: string) {
    return await this.request<ApiTypes.ILoginResponse>(apiRoutes.login, {
      method: 'POST',
      body: createJsonBlob({
        username,
        password,
        application: 'LaborPortal',
        applicationrole: 'AppLaborPortal'
      })
    });
  }

  async logout() {
    return await this.request(apiRoutes.logout, {
      method: 'GET'
    });
  }

  async arbeitsmittel() {
    return await this.request(apiRoutes.arbeitsmittel, {
      method: 'GET'
    });
  }

  async arbeitsmittelDetail(arbeitsmittelId: string) {
    return await this.request<ApiTypes.IArbeitsmittel>(`${apiRoutes.arbeitsmittel}/${arbeitsmittelId}`, {
      method: 'GET'
    });
  }

 async orte(typ: string) {
    return await this.request<ApiTypes.IOrtsListeResponse>(`${apiRoutes.orte}?typ=${typ}`, {
      method: 'GET'
    });
  }

  async profil() {
    return await this.request<ApiTypes.IProfileResponse>(apiRoutes.profil, {
      method: 'GET'
    });
  }

  async freigaben() {
    return await this.request<ApiTypes.IFreigabenResponse>(apiRoutes.freigaben, {
      method: 'GET'
    });
  }

  async personallistekategorie(kategorie: string) {
    return await this.request<ApiTypes.IPersonallisteResponse>(`${apiRoutes.personallisteKategorie}/${kategorie}`, {
      method: 'GET'
    });
  }

  async befugnisse() {
    return await this.request<ApiTypes.IBefugnisListeResponse>(apiRoutes.befugnisse, {
      method: 'GET'
    });
  }

  async dienstleistungen(kategorie: string) {
    return await this.request<ApiTypes.IDienstleistungsListeResponse>(`${apiRoutes.dienstleistungen}?kategorie=${kategorie}`, {
      method: 'GET'
    });
  }

  async auftraege(pageNumber: number, filter: string, status: string, lizenz: boolean) {
    let url = `${apiRoutes.auftraege}?page=${pageNumber}`;
    if (lizenz) {
      url = `${apiRoutes.lizenzauftraege}?page=${pageNumber}`;
    }
    if (filter !== '') {
      url += `&filter=${filter}`;
    }
    if (status !== '') {
      url += `&status=${status}`;
    }
    return await this.request<ApiTypes.IAuftragsListePaginatedResponse>(url, {
      method: 'GET'
    });
  }

  async lizenzauftraege() {
    return await this.request<ApiTypes.IAuftragsListeResponse>(apiRoutes.lizenzauftraege, {
      method: 'GET'
    });
  }

  async auftraegeanzahl(status: string) {
    return await this.request<ApiTypes.IAuftragAnzahlResponse>(`/api/aw/auftraegeanzahl?status=${status}`, {
      method: 'GET'
    });
  }

  async auftraegemesswerte(kategorie: string) {
    return await this.request<ApiTypes.IAuftragsListeResponse>(`/api/labor/auftraege/messwerte/${kategorie}`, {
      method: 'GET'
    });
  }

  async objekte(typ: string, pageNumber: number, filter: string) {
    let url = `${apiRoutes.objekte}?typ=${typ}&page=${pageNumber}`;
    if (filter !== '') {
      url += `&filter=${filter}`;
    }
    return await this.request<ApiTypes.IObjektListePaginatedResponse>(url, {
      method: 'GET'
    });
  }

  async objektVorlagen(kategorie: string) {
    return await this.request<ApiTypes.IObjektVorlagenResponse>(`${apiRoutes.objekte}/vorlagen/${kategorie}`, {
      method: 'GET'
    });
  }

  async objektVorlage(objektGuid: string) {
    return await this.request<ApiTypes.IObjektVorlageResponse>(`/api/aw/objekte/${objektGuid}/vorlage`, {
      method: 'GET'
    });
  }

  async objektStatus(objektGuid: string, status: 'aktiv' | 'gesperrt') {
    return await this.request<ApiTypes.IObjektListeResponse>(`/api/aw/objekte/${objektGuid}/status`, {
      method: 'POST',
      body: createJsonBlob({
          status
        })
    });
  }

  async objektAnlegen(objekt: Partial<ApiTypes.IObjekt>, auftragId?: string, vorgangId?: string) {
    return await this.request<ApiTypes.IObjektAnlegenResponse>(apiRoutes.objekte, {
      method: 'POST',
      body: createJsonBlob({
        objekt: objekt,
        auftragGuid: auftragId,
        vorgangGuid: vorgangId
      })
    });
  }

  async auftragAnlegen(daten: ({})) {
    return await this.request<ApiTypes.IAuftragAnlegenResponse>(apiRoutes.auftraege, {
      method: 'POST',
      body: createJsonBlob(daten)
    });
  }

  async auftragDetailsAaendern(auftragsId: string, daten: ({})) {
    return await this.request<ApiTypes.IAuftragAendernResponse>(`${apiRoutes.auftraege}/${auftragsId}/details`, {
      method: 'POST',
      body: createJsonBlob(daten)
    });
  }

  async auftragAdresseAendern(auftragsId: string, adressArt: ApiTypes.AdressArt, daten: ({})) {
    return await this.request<ApiTypes.IAuftragAendernResponse>(`${apiRoutes.auftraege}/${auftragsId}/adresse/${adressArt}`, {
      method: 'POST',
      body: createJsonBlob(daten)
    });
  }

  async auftraegeAendern(auftragsId: string, auftragName: string, metadaten?: ApiTypes.IAuftragMeta[], li?: string) {
    return await this.request<ApiTypes.IAuftragAendernResponse>(`${apiRoutes.auftraege}/${auftragsId}`, {
      method: 'PUT',
      body: createJsonBlob({
        auftragname: auftragName,
        meta: metadaten,
        lizenznehmer: li
      })
    });
  }

  async auftragDetails(auftragsId: string) {
    return await this.request<ApiTypes.IAuftragDetailsResponse>(`${apiRoutes.auftraege}/${auftragsId}`, {
      method: 'GET'
    });
  }

  async auftragDokumente(auftragId: string) {
    return await this.request<ApiTypes.IDokumenteResponse>(`${apiRoutes.auftraege}/${auftragId}/dokumente`, {
      method: 'GET'
    });
  }

  async auftragDokumentenSuche(auftragId: string, suchbegriff: string) {
    return await this.request<ApiTypes.IDokumentenSucheResponse>(`${apiRoutes.auftraege}/${auftragId}/dokumentensuche/${suchbegriff}`, {
      method: 'GET'
    });
  }

  async auftragFormularDatenSuche(auftragId: string, kuerzel: string) {
    return await this.request<ApiTypes.IFormularDatumSucheResponse>(`${apiRoutes.auftraege}/${auftragId}/formulardatensuchen/${kuerzel}`, {
      method: 'GET'
    });
  }

  async auftragDienstleistungAnlegen(auftragId: string, dienstleistung: string) {
    return await this.request<ApiTypes.IAuftragDienstleistungResponse>(`${apiRoutes.auftraege}/${auftragId}/dienstleistungen`, {
      method: 'PUT',
      body: createJsonBlob({
        dienstleistungen: [
          dienstleistung
        ]
      })
    });
  }

  auftragDokumentDownloadUrl(auftragId: string, dokumentId: string) {
    return `${apiRoutes.auftraege}/${auftragId}/dokumente/${dokumentId}/download?token=${this.token}`;
  }

  async auftragFolgeaAuftragAnlegen(auftragId: string) {
    return await this.request<ApiTypes.IAuftragAbschliessenResponse>(`${apiRoutes.auftraege}/${auftragId}/folgeauftrag`, {
      method: 'POST'
    });
  }

  async auftragAbschliessen(auftragId: string) {
    return await this.request<ApiTypes.IAuftragAbschliessenResponse>(`${apiRoutes.auftraege}/${auftragId}/externabschliessen`, {
      method: 'POST'
    });
  }

  async auftragZurueckstellen(auftragId: string) {
    return await this.request<ApiTypes.IAuftragZuruecksetzenResponse>(`${apiRoutes.auftraege}/${auftragId}/zurueckstellen`, {
      method: 'POST'
    });
  }

  async auftragInArbeit(auftragId: string) {
    return await this.request<ApiTypes.IAuftragInArbeitResponse>(`${apiRoutes.auftraege}/${auftragId}/inarbeit`, {
      method: 'POST'
    });
  }

  async vorgangDetails(vorgangsId: string) {
    return await this.request<ApiTypes.IVorgangDetailsResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}`, {
      method: 'GET'
    });
  }

  async vorgangDokumente(vorgangId: string) {
    return await this.request<ApiTypes.IDokumenteResponse>(`${apiRoutes.vorgaenge}/${vorgangId}/dokumente`, {
      method: 'GET'
    });
  }

  async vorgangThumbnails(vorgangId: string) {
    return await this.request<ApiTypes.IThumbnailsResponse>(`${apiRoutes.vorgaenge}/${vorgangId}/thumbnails`, {
      method: 'GET'
    });
  }

  vorgangThumbnailPreviewUrl(vorgangId: string, thumbnailId: string) {
    return `${apiRoutes.vorgaenge}/${vorgangId}/thumbnails/${thumbnailId}?token=${this.token}`;
  }

  async vorgangKorrigieren(vorgangId: string) {
    return await this.request<ApiTypes.IVorgangKorrigierenResponse>(`${apiRoutes.vorgaenge}/${vorgangId}/korrektur`, {
      method: 'POST'
    });
  }

  vorgangDokumentDownloadUrl(vorgangId: string, dokumentId: string) {
    return `${apiRoutes.vorgaenge}/${vorgangId}/dokumente/${dokumentId}/download?token=${this.token}`;
  }

  async vorgangVorlage(vorgangsId: string) {
    return await this.request<ApiTypes.IVorgangVorlageResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/vorlage`, {
      method: 'GET'
    });
  }

  async vorgangVorlageErzeugen(vorgangsId: string, vorlage: string) {
    return await this.request<ApiTypes.IVorgangVorlageErzeugenResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/vorlage/erzeugen`, {
      method: 'POST',
      body: createJsonBlob({
        vorlage
      })
    });
  }

  async vorgangProtokollUpload(vorgangsId: string, titel: string, protokollDatei: File, vorgangAbschliessen?: boolean) {
    const data = new FormData();
    data.append('file', protokollDatei);
    data.append('titel', titel);
    if (vorgangAbschliessen !== undefined) {
      data.append('abschliessen', vorgangAbschliessen ? 'true' : 'false');
    }
    return await this.request<ApiTypes.IVorgangProtokollUploadResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/vorlage/upload`, {
      method: 'POST',
      body: data
    });
  }

  async vorgangDateiUpload(vorgangsId: string, titel: string, datei: File) {
    const data = new FormData();
    data.append('file', datei);
    data.append('titel', titel);
    return await this.request<ApiTypes.IVorgangProtokollUploadResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/datei/upload`, {
      method: 'POST',
      body: data
    });
  }

  async vorgangFotoUpload(vorgangsId: string, kategorie: string, files: File[]) {
    const data = new FormData();
    for (let i = 0; i < files.length; i++) {
      data.append(`file${i}`, files[i], files[i].name);
    }
    data.append('kategorie', kategorie);
    return await this.request<ApiTypes.IVorgangProtokollUploadResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/foto/upload`, {
      method: 'POST',
      body: data
    });
  }

  async vorgangArbeitsmittel(vorgangsId: string) {
    return await this.request<ApiTypes.IVorgangArbeitsmittelResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/arbeitsmittel`, {
      method: 'GET'
    });
  }

  async vorgangArbeitsmittelEntfernen(vorgangsId: string, arbeitsmittelId: string) {
    return await this.request<ApiTypes.IVorgangArbeitsmittelEntfernenResponse>(
      `${apiRoutes.vorgaenge}/${vorgangsId}/arbeitsmittel/${arbeitsmittelId}`,
      {
        method: 'DELETE'
      }
    );
  }

  async vorgangObjekte(vorgangsId: string) {
    return await this.request<ApiTypes.IVorgangObjekteResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/objekte`, {
      method: 'GET'
    });
  }

  async vorgangOrtSetzen(vorgangsId: string, ortsId: string) {
    return await this.request<ApiTypes.IVorgangOrtSetzenResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/ort`, {
      method: 'POST',
      body: createJsonBlob({
        ortguid: ortsId
      })
    });
  }

  async vorgangObjektSetzen(vorgangsId: string, objektId: string) {
    return await this.request<ApiTypes.IAuftragObjektSetzenResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/objekt`, {
      method: 'POST',
      body: createJsonBlob({
        objektguid: objektId
      })
    });
  }

  async vorgangObjektEntfernen(vorgangsId: string, objektId: string) {
    return await this.request<ApiTypes.IAuftragObjektSetzenResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/objekt`, {
      method: 'DELETE',
      body: createJsonBlob({
        objektguid: objektId
      })
    });
  }

  async vorgangFormularEingaben(vorgangsId: string, formularDaten: string) {
    return await this.request<ApiTypes.IVorgangFormularEingabenResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/formulareingaben`, {
      method: 'POST',
      body: createJsonBlob({
        formulardaten: formularDaten
      })
    });
  }

  async vorgangFormularEingabenVollstaendig(vorgangsId: string) {
    return await this.request<ApiTypes.IVorgangFormularEingabenVollstaendigResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/formulareingabenvollstaendig`, {
      method: 'GET'
    });
  }

  async vorgangAbschliessen(vorgangsId: string) {
    return await this.request<ApiTypes.IVorgangStatusResponse>(`${apiRoutes.vorgaenge}/${vorgangsId}/status/abschliessen`, {
      method: 'POST'
    });
  }

  async auftragObjektSetzen(auftragId: string, objektId: string) {
    return await this.request<ApiTypes.IAuftragObjektSetzenResponse>(`${apiRoutes.auftraege}/${auftragId}/objekt`, {
      method: 'POST',
      body: createJsonBlob({
        objektguid: objektId
      })
    });
  }

  async auftragObjektEntfernen(auftragId: string, objektId: string) {
    return await this.request<ApiTypes.IAuftragObjektSetzenResponse>(`${apiRoutes.auftraege}/${auftragId}/objekt`, {
      method: 'DELETE',
      body: createJsonBlob({
        objektguid: objektId
      })
    });
  }

  async beurteilungFahrzeugAufstellflaeche(messung: ApiTypesSes.ISesMessung) {
    return await this.request<ApiTypes.IBeurteilungFahrzeugAufstellflaecheResponse>(apiRoutes.beurteilungaufstellflaecheFahrzeug, {
      method: 'POST',
      body: createJsonBlob(messung)
    });
  }


  async beurteilungSepAufstellflaeche(messung: ApiTypesSes.ISesMessung) {
    return await this.request<ApiTypes.IBeurteilungSepAufstellflaecheResponse>(apiRoutes.beurteilungaufstellflaecheSep, {
      method: 'POST',
      body: createJsonBlob(messung)
    });
  }

  async erzeugeMessergebnisAuswertung(vorgangId: string, messwerteFahrzeug: ApiTypesSes.ISesMessung, messwerteSep: ApiTypesSes.ISesMessung) {
    return await this.request<ApiTypes.IMessergebnisAuswertungResponse>(apiRoutes.auswertungMesswerte, {
      method: 'POST',
      body: createJsonBlob({
        vorgang: vorgangId,
        messdaten: {
          fahrzeug: messwerteFahrzeug,
          sep: messwerteSep
        }
      })
    });
  }

  async messwerteSesVorhanden(vorgangId: string) {
    return await this.request<ApiTypes.IMesswerteSesVorhandenResponse>(`${apiRoutes.messwerteSesVorhanden}/${vorgangId}`, {
      method: 'GET'
    });
  }

  async ubernehmeMesswerteSes(vorgangId: string, auftragId: string) {
    return await this.request<ApiTypes.IMesswerteSesVorhandenResponse>(`/api/labor/ses/messwerte/${vorgangId}/uebernehmen`, {
      method: 'POST',
      body: createJsonBlob({
        auftrag_quelle: auftragId
      })
    });
  }

  async ergebnisBlockSes(vorgangId: string) {
    return await this.request<ApiTypesSes.ISesErgebnisBlockSystemneigung>(`/api/labor/ses/messwerte/ergebnisblock/${vorgangId}`, {
      method: 'GET'
    });
  }

  async beurteilungaufstellflaecheKalibrierschein(vorgang: string, auftrag?: string) {
    return await this.request<ApiTypes.IBeurteilungAufstellflaecheKalibrierscheinResponse>(apiRoutes.beurteilungaufstellflaecheKalibrierschein, {
      method: 'POST',
      body: createJsonBlob({
        auftrag: auftrag,
        vorgang: vorgang
      })
    });
  }

  async eingabenAuswertungSesSpeichern(vorgangId: string, messwerteFahrzeug: ApiTypesSes.ISesMessung, messwerteSep: ApiTypesSes.ISesMessung) {
    return await this.request<ApiTypes.IApiSuccessDate>(`/api/labor/ses/messwerte/eingabenspeichern/${vorgangId}`, {
      method: 'POST',
      body: createJsonBlob({
        vorgang: vorgangId,
        messdaten: {
          fahrzeug: messwerteFahrzeug,
          sep: messwerteSep
        }
      })
    });
  }

  async eingabenAuswertungSesLaden(vorgangId: string) {
    return await this.request<ApiTypes.IApiSuccessDaten>(`/api/labor/ses/messwerte/eingabenladen/${vorgangId}`, {
      method: 'GET'
    });
  }

  /* Beurteilung SES Richtlinie 2018 */
  async beurteilungFahrzeugAufstellflaecheRili2018(messung: ApiTypesSes.ISesMessung) {
    return await this.request<ApiTypes.IBeurteilungFahrzeugAufstellflaecheResponse>('/api/labor/ses/beurteilungaufstellflaeche/rili2018/fahrzeug', {
      method: 'POST',
      body: createJsonBlob(messung)
    });
  }

  async erzeugeDokumentAuswertungRili2018(vorgangId: string, messwerteFahrzeug: ApiTypesSes.ISesMessung, messwerteSep: ApiTypesSes.ISesMessung) {
    return await this.request<ApiTypes.IMessergebnisAuswertungResponse>('/api/labor/ses/beurteilungaufstellflaeche/rili2018/auswertung', {
      method: 'POST',
      body: createJsonBlob({
        vorgang: vorgangId,
        messdaten: {
          fahrzeug: messwerteFahrzeug,
          sep: messwerteSep
        }
      })
    });
  }

  /* Beurteilung SES Richtlinie 2025 */
  async beurteilungFahrzeugAufstellflaecheRili2025(messung: ApiTypesSes.ISesMessung) {
    return await this.request<ApiTypes.IBeurteilungFahrzeugAufstellflaecheResponse>('/api/labor/ses/beurteilungaufstellflaeche/rili2025/fahrzeug', {
      method: 'POST',
      body: createJsonBlob(messung)
    });
  }

  async erzeugeDokumentAuswertungRili2025(vorgangId: string, messwerteFahrzeug: ApiTypesSes.ISesMessung, messwerteSep: ApiTypesSes.ISesMessung) {
    return await this.request<ApiTypes.IMessergebnisAuswertungResponse>('/api/labor/ses/beurteilungaufstellflaeche/rili2025/auswertung', {
      method: 'POST',
      body: createJsonBlob({
        vorgang: vorgangId,
        messdaten: {
          fahrzeug: messwerteFahrzeug,
          sep: messwerteSep
        }
      })
    });
  }

  /* Sonstiges */
  async auftragNichtErteilt(auftragId: string) {
    return await this.request<ApiTypes.IAuftragNichtErteiltResponse>(`${apiRoutes.auftraege}/${auftragId}/nichterteilt`, {
      method: 'POST'
    });
  }

  async qmdokumentation() {
    return await this.request<ApiTypes.IQmDokumentationResponse>(apiRoutes.qmdokumentation, {
      method: 'GET'
    });
  }

  async qmDokumentDownloadUrl(befugnis: string, dateiname: string) {
    return `${apiRoutes.qmdokumentation}/${befugnis}/${dateiname}?token=${this.token}`;
  }

  async rechnungspositionenDienstleistung(dienstleistung: string) {
    return await this.request<ApiTypes.IRechnungspositionenResponse>(`${apiRoutes.rechnungspositionen}/dienstleistung/${dienstleistung}`, {
      method: 'GET'
    });
  }

  async rechnungErzeugen(vorgangId: string, rechnungspositionen: ApiTypes.IRechnungspositionenResponse, mailRechnungsversand: string, rechnungPostalisch: boolean) {
    return await this.request<ApiTypes.IRechnungErzeugenResponse>(`${apiRoutes.vorgaenge}/${vorgangId}/abrechnen`, {
      method: 'POST',
      body: createJsonBlob({
        positionen: rechnungspositionen,
        mail: mailRechnungsversand,
        postalisch: rechnungPostalisch
      })
    });
  }

  async sammelRechnungErzeugen(vorgangId: string, rechnungspositionen: ApiTypes.IRechnungspositionenResponse) {
    return await this.request<ApiTypes.IRechnungErzeugenResponse>(`${apiRoutes.vorgaenge}/${vorgangId}/sammelabrechnen`, {
      method: 'POST',
      body: createJsonBlob({
        positionen: rechnungspositionen
      })
    });
  }

  async versionsinfo() {
    return await this.request<ApiTypes.IVersionsinfoResponse>(apiRoutes.versionsinfo, {
      method: 'GET'
    });
  }

  async feVersionsInfo() {
    return await this.request<ApiTypes.IFeVersionInfoResponse>('/version.json', {
      method: 'GET'
    });
  }

  async rueckmeldung(betreff: string, nachricht: string) {
    return await this.request<ApiTypes.IRueckmeldungResponse>(`${apiRoutes.rueckmeldung}`, {
      method: 'POST',
      body: createJsonBlob({
        betreff: betreff,
        nachricht: nachricht
      })
    });
  }

  async benachrichtigung() {
    return await this.request<ApiTypes.IBenachrichtigungResponse>('/api/user/benachrichtigung', {
      method: 'GET'
    });
  }

  vorlagenElementImageUrl(elementId: string) {
    return `/api/aw/vorlagen/elemente/${elementId}/image?token=${this.token}`;
  }

  // Öffentliche Routen
  async publicAuftragDokumente(auftragId: string) {
    return await this.request<IPublicAuftrag>(`/api/public/auftrag/${auftragId}/dokumente`, {
      method: 'GET'
    });
  }

  publicAuftragDokumentDownloadUrl(auftragId: string, dokumentId: string) {
    return `/api/public/auftrag/${auftragId}/dokumente/${dokumentId}/download`;
  }

  async publicAuftragAnfrageFolgeAuftrag(auftragId: string) {
      return await this.request<ApiTypes.IApiSuccess>(`/api/public/auftrag/${auftragId}/anfragefolgeauftrag`, {
        method: 'GET'
      });
  }

  /** Kalibrierung BPS Richtlinie */
  async bpsReferenzkraftMesspunkte(messwerte: IBpsReferenzkraftMesswerte) {
    return await this.request<IBpsReferenzkraftMesswerteResponse>(`/api/labor/bps/referenzkraft/messpunkte`, {
      method: 'POST',
      body: createJsonBlob(messwerte)
    });
  }

  async bpsReferenzkraftBeurteilung(messwerte: IBpsReferenzkraftBeurteilungRequest) {
    return await this.request<IBpsReferenzkraftBeurteilungResponse>(`/api/labor/bps/referenzkraft/beurteilung`, {
      method: 'POST',
      body: createJsonBlob(messwerte)
    });
  }

  /** Kalibrierung BPS Richtlinie 2021 */
  async bpsReferenzkraftMesspunkteRili2021(messwerte: IBpsReferenzkraftMesswerte) {
    return await this.request<IBpsReferenzkraftMesswerteResponse>(`/api/labor/bps/referenzkraft/2021/messpunkte`, {
      method: 'POST',
      body: createJsonBlob(messwerte)
    });
  }

  async bpsReferenzkraftBeurteilungRili2021(messwerte: ApiTypesBps2021.IBpsReferenzkraftRolleMessung) {
    return await this.request<ApiTypesBps2021.IBpsReferenzkraftBeurteilungResponse>(`/api/labor/bps/referenzkraft/2021/beurteilung`, {
      method: 'POST',
      body: createJsonBlob(messwerte)
    });
  }

  /** Kalibrierung BPS Platte */
  async bpsReferenzkraftMesspunktePlatte(messwerte: ApiTypesBpsPlatte.IBpsReferenzkraftMesswerte) {
    return await this.request<IBpsReferenzkraftMesswerteResponse>(`/api/labor/bps/referenzkraft/platte/messpunkte`, {
      method: 'POST',
      body: createJsonBlob(messwerte)
    });
  }

  async bpsReferenzkraftBeurteilungPlatte(messwerte: ApiTypesBpsPlatte.IBpsReferenzkraftMessungPlatte) {
    return await this.request<ApiTypesBpsPlatte.IBpsReferenzkraftBeurteilungResponse>(`/api/labor/bps/referenzkraft/platte/beurteilung`, {
      method: 'POST',
      body: createJsonBlob(messwerte)
    });
  }

  /** Kalibrierung AGT Partikelzähler */
  async agtPartikelzaehlerChargingBeurteilung(messwerte: ApiTypesAgtPartikelzaehler.IAgtPartikelzaehlerMessung) {
    return await this.request<ApiTypesAgtPartikelzaehler.IAgtPartikelzaehlerBeurteilungResponse>(`/api/labor/agt/partikelzaehler/beurteilung`, {
      method: 'POST',
      body: createJsonBlob(messwerte)
    });
  }

  /** Routen für Abruf der Stammdaten  */

  async untersuchungsstelleVorhanden(id: string) {
    return await this.request<ApiTypes.IAdresseVorhandenResponse>(`/api/stammdaten/untersuchungsstelle/${id}/vorhanden`, {
      method: 'GET'
    });
  }

  async untersuchungsstelleAdresse(id: string) {
    return await this.request<ApiTypes.IAdresseDatenResponse>(`/api/stammdaten/untersuchungsstelle/${id}/adresse`, {
      method: 'GET'
    });
  }

  async adresseVorhanden(id: string) {
    return await this.request<ApiTypes.IAdresseVorhandenResponse>(`/api/stammdaten/adresse/${id}/vorhanden`, {
      method: 'GET'
    });
  }

  async adresseDaten(id: string) {
    return await this.request<ApiTypes.IAdresseDatenResponse>(`/api/stammdaten/adresse/${id}/daten`, {
      method: 'GET'
    });
  }

  async messmittelVorhanden(kategorie: string, id: string) {
    return await this.request<ApiTypes.IMessmittelVorhandenResponse>(`/api/stammdaten/messmittel/${kategorie}/${id}/vorhanden`, {
      method: 'GET'
    });
  }

  async messmittelDaten(kategorie: string, id: string) {
    return await this.request<ApiTypes.IMessmittelDatenResponse>(`/api/stammdaten/messmittel/${kategorie}/${id}/daten`, {
      method: 'GET'
    });
  }

  async rechnungsuebersicht(pageNumber: number, filter: string, status: number | null, art: string | null) {
    let url = `${apiRoutes.rechnungsuebersicht}?page=${pageNumber}`;
    if (filter !== '') {
      url += `&filter=${filter}`;
    }
    if (status !== null) {
      url += `&status=${status}`;
    }
    if (art !== null) {
      url += `&art=${art}`;
    }
    return await this.request<ApiTypes.IRechnungListePaginatedResponse>(url, {
      method: 'GET'
    });
  }

  async rechnungDetail(id: string) {
    return await this.request<ApiTypes.IRechnungDetailsResponse>(`${apiRoutes.rechnungsdetail}/${id}`, {
      method: 'GET'
    });
  }

  async rechnungenAuftrag(anr: string) {
    let url = `${apiRoutes.rechnungenauftrag}/${anr}`;
    return await this.request<ApiTypes.IRechnung[]>(url, {
      method: 'GET'
    });
  }

}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/stores/ApiStore.ts